import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import BackIcon from '../assets/icons/back-icon.inline.svg';
import Button from './Button';
import { useFormikContext } from 'formik';
import clsx from 'clsx';
import useDataLayer from '../hooks/useDataLayer';
import get from 'lodash/get';
import { useIntl } from 'react-intl';
import messages from '../messages/common';

const StepNavigation = ({
  className = '',
  onBackClick,
  children,
  backButtonMessage,
  submitButtonMessage,
  dataLayerPushValueOnSubmit,
  dataLayerPushOnBack,
  dataLayerPushOnContinue,
  step,
  portalId,
  isNotCompleted = false,
  showIcon = true,
  disabled = false,
  isCustomerReceivables = false,
  style,
  buttonClassName,
  backButtonClassName,
  mobileInline = false,
}) => {
  const { formatMessage } = useIntl();
  const { isValid, isSubmitting, values, submitForm } =
    useFormikContext() || {};

  const [pushEvent] = useDataLayer();

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    portalId && setMounted(true);
    // eslint-disable-next-line
  }, []);

  function onBack() {
    onBackClick();
    if (dataLayerPushOnBack && process.env.NODE_ENV !== 'test') {
      pushEvent({
        eventLabel: 'back',
        eventAction: step,
      });
    }
  }

  function onSubmit() {
    const customerEmail = JSON.parse(
      localStorage.getItem('yo:compensationCheck')
    )?.value?.email;
    if (dataLayerPushValueOnSubmit && process.env.NODE_ENV !== 'test') {
      pushEvent({
        eventLabel: get(values, dataLayerPushValueOnSubmit),
        eventAction: step,
        customerEmail,
      });
    } else if (dataLayerPushOnContinue && process.env.NODE_ENV !== 'test') {
      pushEvent({
        eventLabel: 'continue',
        eventAction: step,
        customerEmail,
      });
    }
    if (portalId) {
      submitForm();
    }
  }
  const checkDisabe = () => {
    if (isCustomerReceivables) {
      return disabled;
    }

    return !isValid || isSubmitting || isNotCompleted || disabled;
  };

  // When there is both a back and a submit button, lay them out inline on
  // mobile (small icon-only back button on the left) to match the desktop
  // layout. Can be forced on via the `mobileInline` prop.
  const inlineMobile = mobileInline || Boolean(onBackClick);

  const component = (
    <div
      className={`${className} text-center mx-15 sm:mx-0 my-25 sm:my-40 mb-30 flex sm:inline-flex ${
        inlineMobile
          ? 'flex-row justify-between items-center'
          : 'flex-col-reverse'
      } sm:flex-row w-auto sm:w-auto`}
      style={style}
    >
      {onBackClick && (
        <button
          type="button"
          data-testid="navigate-back"
          disabled={isSubmitting}
          onClick={onBack}
          style={inlineMobile ? { width: '50px' } : undefined}
          className={clsx(
            'back-button flex-shrink-0 font-pt-sans flex sm:inline-flex items-center text-center justify-center button sm:border text-center rounded-full sm:w-50 h-50 text-gray-300 border-gray-300 hover:bg-gray-300 outline-none focus:outline-none',
            { border: inlineMobile },
            {
              'text-lg': !/(^|\s)text-(xxs|xs|sm|md|base|root|lg|xl|xll|24|xxl|3xl|4xl|enormous)(\s|$)/.test(
                backButtonClassName || ''
              ),
            },
            { 'opacity-50 cursor-default': isSubmitting },
            backButtonClassName
          )}
        >
          <BackIcon
            className={clsx('sm:inline', inlineMobile ? 'inline' : 'hidden')}
          />
          <span
            className={clsx(
              'outline-none focus:outline-none',
              inlineMobile ? 'hidden' : 'sm:hidden'
            )}
          >
            {backButtonMessage || formatMessage(messages.back)}
          </span>
        </button>
      )}
      {children}
      <Button
        type="submit"
        onClick={onSubmit}
        theme="primary"
        disabled={checkDisabe()}
        isSubmitting={isSubmitting}
        showIcon={showIcon}
        className={clsx(buttonClassName, {
          'h-50 ml-15 sm:ml-30': inlineMobile,
        })}
        style={inlineMobile ? { marginBottom: 0 } : undefined}
      >
        {submitButtonMessage}
      </Button>
    </div>
  );

  if (mounted && portalId) {
    const portal = document?.getElementById(portalId);
    return createPortal(component, portal);
  }

  return component;
};

StepNavigation.propTypes = {
  backButtonMessage: PropTypes.string,
  children: PropTypes.any,
  className: PropTypes.string,
  dataLayerPushOnBack: PropTypes.bool,
  dataLayerPushOnContinue: PropTypes.bool,
  dataLayerPushValueOnSubmit: PropTypes.string,
  mobileInline: PropTypes.bool,
  onBackClick: PropTypes.func,
  portalId: PropTypes.string,
  showIcon: PropTypes.bool,
  step: PropTypes.string,
  submitButtonMessage: PropTypes.string,
};

export default StepNavigation;
